import React, { useState } from "react";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../api/client";
import { GetCrmPositionsResponse, SchoolContactWithPermissionResponse } from "../../api/PartnerPortalGatewayApi";
import { useNotification } from "../../contexts/NotificationContext";
import { CADialog } from "../ui";

interface SchoolContactListProps {
  serviceId: number;
  positions: GetCrmPositionsResponse[];
  onEditContact: (contact: SchoolContactWithPermissionResponse) => void;
  isReadOnly?: boolean;
}

export const SchoolContactList: React.FC<SchoolContactListProps> = ({ serviceId, positions, onEditContact, isReadOnly = false }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<SchoolContactWithPermissionResponse | null>(null);

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  // Query for school contacts
  const {
    data: contacts = [],
    isLoading: contactsLoading,
    error: contactsError,
  } = useQuery({
    queryKey: ["school-contacts", serviceId],
    queryFn: () => apiClient.schoolContact.getSchoolContactsByServiceId(serviceId),
    staleTime: 0,
    gcTime: 0,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (contactId: number) => apiClient.schoolContact.deleteSchoolContact(serviceId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["school-contacts", serviceId] });
      setIsDeleteDialogOpen(false);
      setContactToDelete(null);
      showSuccess("Contact deleted successfully");
    },
    onError: (error) => {
      console.error("Error deleting contact:", error);
      showError("Failed to delete contact");
    },
  });

  const loading = contactsLoading;
  const error = contactsError;

  const handleEditContact = (contact: SchoolContactWithPermissionResponse) => {
    onEditContact(contact);
  };

  const handleDeleteContact = (contact: SchoolContactWithPermissionResponse) => {
    setContactToDelete(contact);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;
    deleteMutation.mutate(contactToDelete.schoolContactId);
  };

  const getPositionName = (positionId?: number) => {
    if (!positionId) return "Unknown Position";
    const position = positions.find((p) => p.positionId === positionId);
    return position?.name || "Unknown Position";
  };

  const getDisplayPermissions = (contactPermissions?: string[]) => {
    const fixedPermissions = ["News", "Attendance Data", "Escalation Path"];
    const additionalPermissions = contactPermissions || [];

    // Return both fixed and additional permissions in the display
    return {
      fixed: fixedPermissions,
      additional: additionalPermissions,
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading school contacts. Please try refreshing the page.</Alert>;
  }

  return (
    <Box>
      {contacts.length === 0 ? (
        <Alert severity="info">
          No school contacts found for this service. Click "Add Contact" to create the first one.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {contacts.map((contact) => {
            const permissions = getDisplayPermissions(contact.permissions);
            const avatarInitials = `${contact.firstName?.charAt(0) || ""}${contact.lastName?.charAt(0) || ""}`;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={contact.schoolContactId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    borderRadius: "16px",
                    border: "2px solid",
                    borderColor: "grey.300",
                    transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: 4,
                      borderColor: "primary.main",
                    },
                  }}
                >
                  {contact.isPrimaryContact && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 1,
                      }}
                    >
                      <Tooltip title="Primary Contact">
                        <StarIcon color="secondary" fontSize="small" />
                      </Tooltip>
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar sx={{ bgcolor: "secondary.main" }}>{avatarInitials}</Avatar>
                      <Box>
                        <Typography variant="h6" component="h3" fontWeight="medium">
                          {contact.firstName} {contact.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getPositionName(contact.positionId)}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Box mb={2}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {contact.email}
                        </Typography>
                      </Box>
                      {contact.phone && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.phone}</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Access
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: "0.85rem" }}>
                        {permissions.fixed.join(", ")}
                        {permissions.additional.length > 0 && `, ${permissions.additional.join(", ")}`}
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditContact(contact)} 
                      color="secondary"
                      disabled={isReadOnly}
                      title={isReadOnly ? "Read-only access: cannot edit" : "Edit contact"}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteContact(contact)} 
                      color="error"
                      disabled={isReadOnly}
                      title={isReadOnly ? "Read-only access: cannot delete" : "Delete contact"}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <CADialog
        open={isDeleteDialogOpen}
        onClose={() => !deleteMutation.isPending && setIsDeleteDialogOpen(false)}
        title="Confirm Delete"
        maxWidth="sm"
        onConfirm={confirmDelete}
        confirmText={deleteMutation.isPending ? "Deleting..." : "Delete"}
        confirmColor="error"
        isLoading={deleteMutation.isPending}
      >
        <Typography>
          Are you sure you want to delete {contactToDelete?.firstName} {contactToDelete?.lastName}? This action cannot
          be undone.
        </Typography>
      </CADialog>
    </Box>
  );
};
